import { Injectable, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { BehaviorSubject, ReplaySubject, distinctUntilChanged, takeUntil } from 'rxjs';

import { LaunchDarklyFlags } from 'app/core/services/launch-darkly/launch-darkly.types';
import { coreSelectors, getFeatureFlagObservable } from 'app/core/state';
import { Loggable } from 'app/utils/logging/loggable';
import User from 'entity/User';
import {
  IncomingMessageType,
  IncomingMessage,
  OutgoingMessageType,
  SpeechTextMessage,
  BaseUser,
} from 'messages/websocket.messages';

import { socialAppSelectors } from '../../state/app';
import { GalleryService } from '../gallery/gallery.service';
import { SceneService } from '../scene.service';
import { WebsocketService } from '../websocket.service';

import { ClosedCaptionMessage } from './../../../../../messages/websocket.messages';

export interface ClosedCaptionItem {
  userId: string;
  text: string;
  user: User;
}

@Injectable({
  providedIn: 'root',
})
export class ClosedCaptionsService extends Loggable implements OnDestroy {
  private user?: User;
  private userId?: string;

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);
  private closedCaptions = new BehaviorSubject<ClosedCaptionItem[]>([]);
  public closedCaptionsStream$ = this.closedCaptions.asObservable();

  public closedCaptionsHistory: ClosedCaptionItem[] = [];
  public closedCaptionsEnabled = true;

  public closedCaptionsPresentation = false;
  private baseUsersList: BaseUser[] = [];

  public constructor(
    private store: Store,
    private websocketService: WebsocketService,
    private sceneService: SceneService,
    private galleryService: GalleryService,
  ) {
    super();
    this.setDisplayName('ClosedCaptionsService');

    this.websocketService.message$.pipe(takeUntil(this.destroyed$)).subscribe(this.handleWebsocketMessage);

    this.closedCaptionsStream$.pipe(takeUntil(this.destroyed$)).subscribe((resp) => {
      if (resp.length) {
        this.closedCaptionsHistory = resp;
      }
    });

    this.store
      .select(socialAppSelectors.isPresentationActive)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isPresentationActive) => (this.closedCaptionsPresentation = isPresentationActive));

    this.store
      .select(coreSelectors.getUser)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((user) => {
        this.user = user.user;
        this.userId = this.user?.userId;
      });

    this.galleryService.allBaseUsers$
      .pipe(distinctUntilChanged(), takeUntil(this.destroyed$))
      .subscribe((baseUsersList) => {
        this.baseUsersList = baseUsersList;
      });

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.closed_captions)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        this.initializeClosedCaptionsListener();
      });
  }

  public ngOnDestroy(): void {
    this.galleryService.destroy();
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  public initializeClosedCaptionsListener(): void {
    this.closedCaptionsEnabled = false;
    this.closedCaptionsHistory = [];
  }

  public toggleClosedCaptions(): void {
    // Clear captions before disabling
    if (this.closedCaptionsEnabled) {
      this.closedCaptionsHistory = [];
      this.closedCaptions.next(this.closedCaptionsHistory);
    }
    this.closedCaptionsEnabled = !this.closedCaptionsEnabled;
  }

  public getCaptionHistory(): ClosedCaptionItem[] {
    return this.closedCaptionsHistory;
  }

  private addCaptionItem(captionItem: ClosedCaptionItem): void {
    const captionHistory = this.closedCaptionsHistory;
    captionHistory.push(captionItem);
    //Keeps the array with 10 items while adding more items
    if (captionHistory.length >= 10) {
      captionHistory.shift();
    }
    this.closedCaptions.next(captionHistory);
  }

  private treatClosedCaption(msg: ClosedCaptionMessage): void {
    if (msg && this.closedCaptionsEnabled) {
      let addCaption = true;
      //If open mode, get users only from participant list
      const usersList = this.baseUsersList.length ? this.baseUsersList : this.sceneService.getAllUsers();
      const speakingUser = usersList.find((player) => player.userId === msg.userId);

      if (!this.closedCaptionsPresentation) {
        if (!this.isUserInPlayerGroup(msg.userId)) {
          addCaption = false;
        }
      }

      if (speakingUser && addCaption) {
        const captionItem: ClosedCaptionItem = {
          userId: msg.userId,
          text: msg.text,
          user: speakingUser,
        };
        this.addCaptionItem(captionItem);
      }
    }
  }

  private isUserInPlayerGroup(userToCheck: string): boolean {
    const groups = this.sceneService.getGroups();
    let isUserInPlayerGroup = false;

    groups.forEach((group) => {
      const groupPlayersIds = group?.players as string[];
      isUserInPlayerGroup = groupPlayersIds?.includes(this.userId || '') && groupPlayersIds?.includes(userToCheck);
      if (isUserInPlayerGroup) {
        return;
      }
    });
    return isUserInPlayerGroup;
  }

  // Send user transcription to captions
  public sendSentenceToCaptions(text: string) {
    if (text && text.length > 0) {
      try {
        const msg: SpeechTextMessage = {
          t: OutgoingMessageType.SPEECH_TEXT,
          text,
        };
        this.websocketService.sendMessage(msg);
      } catch (e) {
        this.error('Failed to send message to captions', e);
      }
    }
  }

  private handleWebsocketMessage = (msg: IncomingMessage): void => {
    const { t: type } = msg;
    switch (type) {
      case IncomingMessageType.CC: {
        this.treatClosedCaption(msg);
      }
    }
  };
}
